/* ==========================================================================
   Ladnini — click-to-zoom for the stacked product gallery
   (snippets/product-gallery-stacked.liquid)
   ==========================================================================
   Small, dependency-free lightbox. Deliberately independent of the theme's
   PhotoSwipe wiring in product.js — that wiring is scoped inside
   <product-slider> and reads Flickity's active-cell state, neither of
   which exists in this layout. Only runs when a [data-zoom] link is
   present on the page.

   Wired document-wide (not just inside .product-gallery-stacked) so the
   same overlay also serves the buy-box color palette's "click to
   enlarge" texture preview (see initPalette below) — one lightbox
   instance, one visual behavior, wherever a [data-zoom] link appears. */
(function () {
  function initGallery(gallery) {
    var links = gallery.querySelectorAll('[data-zoom]');
    if (!links.length) return;

    var overlay = document.createElement('div');
    overlay.className = 'ladnini-stacked-lightbox';
    overlay.innerHTML =
      '<button type="button" class="ladnini-stacked-lightbox__close" aria-label="Zamknij powiększenie">&times;</button>' +
      '<img class="ladnini-stacked-lightbox__img" alt="">';
    document.body.appendChild(overlay);

    var img = overlay.querySelector('.ladnini-stacked-lightbox__img');
    var closeBtn = overlay.querySelector('.ladnini-stacked-lightbox__close');
    var lastFocused = null;

    function open(link) {
      lastFocused = document.activeElement;
      img.src = link.getAttribute('href');
      img.alt = link.getAttribute('aria-label') || '';
      overlay.classList.add('is-open');
      document.body.classList.add('ladnini-stacked-lightbox-open');
      closeBtn.focus();
    }

    function close() {
      overlay.classList.remove('is-open');
      document.body.classList.remove('ladnini-stacked-lightbox-open');
      img.src = '';
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
    }

    links.forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        open(link);
      });
    });

    closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) close();
    });
  }

  /* ------------------------------------------------------------------
     Height guard: keep the whole product section's height tied to the
     gallery only — never let the buy box's own content (open accordion,
     trust icons, collection, palette) grow the section itself. Instead
     cap the buy box at the gallery's height and let IT scroll
     internally when its content is taller than that.
     ------------------------------------------------------------------
     The theme's own CSS sizes the product-info row around a single
     slider slide, not a tall stack of full photos — so however that
     sizing actually works internally (sticky, absolute, a flex/grid
     assumption, whatever it turns out to be), the section box can end
     up shorter than the gallery really is. That's what let the next
     section on the page start before this one had visually finished,
     which is what the section's own overflow: hidden then clipped
     instead of showing as an overlap. Measuring the gallery directly
     and pushing a matching min-height onto the section wrapper fixes
     the actual cause instead of just hiding the symptom, and it doesn't
     require knowing which theme rule was responsible.

     The buy box has since grown (trust icons, a collection carousel,
     the color palette) and can now end up taller than the gallery too.
     Letting the whole section grow to match it (an earlier version of
     this file) technically avoided clipping, but pushed every section
     after this one further down the page whenever an accordion opened —
     not what was wanted; the section's own length shouldn't depend on
     whether a customer has a belka open. Capping the buy box at the
     gallery's own height and letting it scroll internally keeps the
     section's height stable and still makes every bit of buy-box
     content reachable. (An earlier attempt at exactly this seemed to
     fail the same way, but that turned out to be a different bug — the
     buy box's own theme-provided max-height override had accidentally
     been dropped from this file, so the *outer* <scroll-shadow> wrapper
     was quietly re-capping things again from further up; that's fixed
     now, in the CSS rule right above the one this cap lives in.)

     Mobile (< 750px): the gallery is now a single-slide, left-right
     swipeable carousel (see ladnini-product-gallery-stacked.css), not a
     tall stack of full photos — its own natural height is now short and
     roughly constant (one 4:5 slide), nowhere near tall enough to hold
     the whole buy box underneath it. Applying the same min-height/
     max-height cap there would squeeze the entire buy box (title,
     price, variant picker, accordion rows, trust icons, palette, buy
     box collection, etc.) into a tiny internally-scrolling box directly
     under a small photo — exactly backwards from what "galleria
     przewijana, pod spodem dalsze sekcje" asked for. Below 750px this
     just clears both inline styles and lets the section/buy box sit in
     normal document flow instead. */
  function initHeightGuard(gallery) {
    var section = gallery.closest('.thb-product-detail');
    if (!section) return;
    var infoContainer = section.querySelector('.product-information');
    var desktopQuery = window.matchMedia('(min-width: 750px)');

    function sync() {
      if (!desktopQuery.matches) {
        section.style.minHeight = '';
        if (infoContainer) {
          infoContainer.style.maxHeight = '';
        }
        return;
      }
      var galleryHeight = gallery.getBoundingClientRect().height;
      section.style.minHeight = galleryHeight + 'px';
      if (infoContainer) {
        infoContainer.style.maxHeight = galleryHeight + 'px';
      }
    }

    sync();
    window.addEventListener('resize', sync);

    if (desktopQuery.addEventListener) {
      desktopQuery.addEventListener('change', sync);
    } else if (desktopQuery.addListener) {
      // Older Safari.
      desktopQuery.addListener(sync);
    }

    if (window.ResizeObserver) {
      new ResizeObserver(sync).observe(gallery);
    }

    // Images swap from a small placeholder to full size as they load,
    // changing the gallery's natural height slightly — re-sync as each
    // one finishes.
    gallery.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) {
        img.addEventListener('load', sync, { once: true });
      }
    });

    // Opening/closing an accordion row changes the buy box's own height
    // without touching the gallery at all, so neither the resize
    // listener nor the ResizeObserver above would notice on their own —
    // re-sync directly on toggle instead.
    if (infoContainer) {
      infoContainer.querySelectorAll('details').forEach(function (details) {
        details.addEventListener('toggle', sync);
      });
    }
  }

  /* ------------------------------------------------------------------
     Pin the "Opis" (description) row permanently open.
     ------------------------------------------------------------------
     Chosen variant C from the buy-box mockup: description reads as a
     fixed panel rather than another collapsible row. There's no reliable
     class name to target it by (it's rendered the same way as every
     other collapsible row), so this finds it by its visible heading
     text instead, marks it with a class the CSS styles differently, and
     blocks the click that would otherwise let it be toggled shut again.
     Scoped to the buy box inside the stacked layout only. */
  function pinDescriptionOpen(root) {
    var rows = root.querySelectorAll('.product-information details');
    rows.forEach(function (row) {
      var summary = row.querySelector('summary');
      if (!summary) return;
      var label = (summary.textContent || '').trim().toLowerCase();
      if (label.indexOf('opis') !== 0) return;

      row.classList.add('ladnini-desc-plakietka');
      row.open = true;
      summary.addEventListener('click', function (e) {
        e.preventDefault();
      });
    });
  }

  /* ------------------------------------------------------------------
     Gallery carousel: thumbnail rail + prev/next arrows (desktop),
     sliding .track with a real slide-in/slide-out transition instead of
     an instant cut between photos.
     ------------------------------------------------------------------
     .track (see ladnini-product-gallery-stacked.css) lays every slide
     out full-width in a row; on desktop this function moves it with a
     pixel transform (measured from the real viewport width, the same
     approach initBuyboxCarousel below already uses for its own
     carousel) rather than a CSS percentage, since a percentage
     transform is relative to .track's OWN width — which is N times the
     viewport once every slide sits side by side — not to one slide's
     width. On mobile the same .track is instead a native scroll-snap
     strip (see the max-width: 749px block in that CSS file) that the
     visitor swipes directly; desktopQuery keeps this function from ever
     setting a transform there; so the two never fight over the same
     element. One slide and one thumbnail carry .is-active at a time,
     both matched by their shared data-index — used for the thumbnail
     rail's own highlight, not for showing/hiding slides (that's what
     the transform does now). Harmless to run unconditionally even for
     a single-photo gallery: that markup has no thumbs/arrows/track at
     all (see the liquid snippet's stacked_images_size > 1 guard), so
     the querySelectorAll calls below just come back empty and the
     length guard returns immediately. */
  function initCarousel(gallery) {
    var thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
    var slides = gallery.querySelectorAll('[data-gallery-slide]');
    var track = gallery.querySelector('[data-gallery-track]');
    var viewport = gallery.querySelector('[data-gallery-slides]');
    var prevBtn = gallery.querySelector('[data-gallery-prev]');
    var nextBtn = gallery.querySelector('[data-gallery-next]');
    if (!track || !viewport || slides.length < 2) return;

    var desktopQuery = window.matchMedia('(min-width: 750px)');
    var index = 0;
    // How much of the PREVIOUS slide peeks in on the left — must match
    // the 56px subtracted (once, not twice — left-only) from each
    // slide's width in ladnini-product-gallery-stacked.css.
    var PEEK = 56;

    function applyPosition() {
      if (!desktopQuery.matches) {
        // Mobile: .track is the native scroll-snap strip instead —
        // clear any transform left over from a desktop-width visit
        // (e.g. rotating a tablet, or resizing the browser) so it
        // doesn't fight the visitor's own swipe scrolling.
        track.style.transform = '';
        return;
      }
      // Each slide is (viewport width - PEEK) wide (see the CSS), so
      // moving the track by exactly that per slide, offset by +PEEK,
      // lands the active slide's own left edge PEEK px from the
      // viewport's left edge (revealing that same sliver of the
      // previous slide) while its right edge lands exactly on the
      // viewport's right edge — no matching gap held back on that side,
      // so the next slide never peeks in.
      var slideWidth = viewport.clientWidth - PEEK;
      track.style.transform = 'translateX(' + (PEEK - index * slideWidth) + 'px)';
    }

    function goTo(newIndex) {
      if (newIndex < 0) newIndex = slides.length - 1;
      if (newIndex >= slides.length) newIndex = 0;
      index = newIndex;

      slides.forEach(function (slide) {
        slide.classList.toggle('is-active', Number(slide.getAttribute('data-index')) === index);
      });
      thumbs.forEach(function (thumb) {
        var isActive = Number(thumb.getAttribute('data-index')) === index;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      applyPosition();
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        goTo(Number(thumb.getAttribute('data-index')));
      });
    });

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        goTo(index - 1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        goTo(index + 1);
      });
    }

    // The viewport's own pixel width is what the transform is measured
    // against, so a browser resize (or a phone rotating past the
    // desktop/mobile breakpoint) needs a re-apply, not just the first
    // measurement at load.
    window.addEventListener('resize', applyPosition);
    if (desktopQuery.addEventListener) {
      desktopQuery.addEventListener('change', applyPosition);
    } else if (desktopQuery.addListener) {
      // Older Safari.
      desktopQuery.addListener(applyPosition);
    }

    applyPosition();
  }

  /* ------------------------------------------------------------------
     Buy-box collection carousel arrows.
     ------------------------------------------------------------------
     The carousel itself is plain CSS (overflow-x + scroll-snap, see
     ladnini-product-gallery-stacked.css) — swipeable on touch and
     draggable with a trackpad with no JS at all. This just adds visible
     prev/next buttons on top: each click scrolls by exactly one card's
     width (measured from the first card, so it stays correct regardless
     of viewport size), and a card's worth of overscroll at either end
     hides the matching arrow instead of leaving it clickable with
     nothing left to scroll to. */
  function initBuyboxCarousel(wrapper) {
    var track = wrapper.querySelector('[data-buybox-carousel]');
    var prevBtn = wrapper.querySelector('[data-buybox-prev]');
    var nextBtn = wrapper.querySelector('[data-buybox-next]');
    if (!track || !prevBtn || !nextBtn) return;

    function step() {
      var firstItem = track.querySelector('.product-buybox-collection__item');
      if (!firstItem) return track.clientWidth;
      var style = window.getComputedStyle(track);
      var gap = parseFloat(style.columnGap || style.gap || '0') || 0;
      return firstItem.getBoundingClientRect().width + gap;
    }

    function updateArrows() {
      var maxScroll = track.scrollWidth - track.clientWidth;
      prevBtn.classList.toggle('is-hidden', track.scrollLeft <= 1);
      nextBtn.classList.toggle('is-hidden', track.scrollLeft >= maxScroll - 1);
    }

    prevBtn.addEventListener('click', function () {
      track.scrollBy({ left: -step(), behavior: 'smooth' });
    });
    nextBtn.addEventListener('click', function () {
      track.scrollBy({ left: step(), behavior: 'smooth' });
    });

    track.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    updateArrows();
  }

  /* ------------------------------------------------------------------
     Color/texture palette (buy box).
     ------------------------------------------------------------------
     Swatches sit in a fixed grid, and hovering the big preview shows an
     immediate magnifier lens — a circle that follows the cursor, filled
     with the SAME photo at a much larger background-size, its
     background-position computed from the cursor's position within the
     stage so the zoomed crop under the lens lines up with what's under
     the cursor on the base image. Tapping the preview (no hover on
     touch) still opens the same fullscreen lightbox the product gallery
     photos use (initGallery/[data-zoom]) as a fallback. Clicking a
     swatch swaps the base image, the lightbox's target, and — if the
     cursor is already over the stage — the lens' own image too. */
  function initPalette(root) {
    var swatches = root.querySelectorAll('[data-palette-swatch]');
    var stage = root.querySelector('[data-palette-stage]');
    var stageImg = root.querySelector('[data-palette-stage-img]');
    var lens = root.querySelector('[data-palette-lens]');
    if (!swatches.length || !stage || !stageImg) return;

    var ZOOM = 2.5;

    function setLensImage(url) {
      if (!lens) return;
      lens.style.backgroundImage = 'url("' + url + '")';
    }

    function moveLens(e) {
      if (!lens) return;
      var rect = stage.getBoundingClientRect();
      var x = e.clientX - rect.left;
      var y = e.clientY - rect.top;
      var ratioX = x / rect.width;
      var ratioY = y / rect.height;

      var lensRect = lens.getBoundingClientRect();
      var bgWidth = rect.width * ZOOM;
      var bgHeight = rect.height * ZOOM;

      lens.style.backgroundSize = bgWidth + 'px ' + bgHeight + 'px';
      lens.style.backgroundPosition =
        -(ratioX * bgWidth - lensRect.width / 2) + 'px ' +
        -(ratioY * bgHeight - lensRect.height / 2) + 'px';
      lens.style.left = x + 'px';
      lens.style.top = y + 'px';
    }

    if (lens) {
      setLensImage(stage.getAttribute('href'));
      stage.addEventListener('mouseenter', function () {
        lens.classList.add('is-visible');
      });
      stage.addEventListener('mousemove', moveLens);
      stage.addEventListener('mouseleave', function () {
        lens.classList.remove('is-visible');
      });
    }

    swatches.forEach(function (btn) {
      btn.addEventListener('click', function () {
        swatches.forEach(function (b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');

        var mediumUrl = btn.getAttribute('data-medium');
        var fullUrl = btn.getAttribute('data-full');
        var name = btn.getAttribute('data-name') || '';

        stageImg.src = mediumUrl;
        stageImg.alt = name;
        stage.setAttribute('href', fullUrl);
        stage.setAttribute('aria-label', 'Powiększ teksturę: ' + name);
        setLensImage(fullUrl);
      });
    });
  }

  document.querySelectorAll('.product-gallery-stacked').forEach(function (gallery) {
    // Pin "Opis" open *before* the height guard's first measurement, so
    // that initial measurement already accounts for it being open —
    // rather than measuring a shorter buy box and hoping the guard's
    // "toggle" listener (added inside initHeightGuard) catches the
    // change a moment later.
    var section = gallery.closest('.product-grid-container--gallery-stacked');
    if (section) pinDescriptionOpen(section);

    initHeightGuard(gallery);
    initCarousel(gallery);
  });

  // Single, document-wide lightbox instance (see comment at top of file).
  initGallery(document);

  document.querySelectorAll('.product-buybox-collection__carousel').forEach(function (wrapper) {
    initBuyboxCarousel(wrapper);
  });

  document.querySelectorAll('[data-palette]').forEach(function (root) {
    initPalette(root);
  });
})();